import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { MaintenanceAppointmentModel, MaintenanceModel } from 'api/models';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { URL_QUERY_PARAMS } from 'maintenance/constants';
import { needsWorkCompletionPrompt } from 'maintenance/utils/appointments';
import { AppointmentOutcomeModal } from '../AppointmentOutcomeModal/appointment-outcome-modal';
import { WorkCompletionPrompt } from '../MaintenanceScheduleComponent/WorkCompletionPrompt/work-completion-prompt';

type WorkCompletionPromptsProps = {
  appointments: MaintenanceAppointmentModel[];
  maintenance: MaintenanceModel;
  isRenter?: boolean;
};

const getPartnerName = (appointment: MaintenanceAppointmentModel) =>
  (appointment.pro?.name ? appointment.pro : appointment.partner)?.name;

export const WorkCompletionPrompts = ({
  appointments = [],
  maintenance,
  isRenter,
}: WorkCompletionPromptsProps) => {
  const { search } = useLocation();
  const history = useHistory();
  const { isEnabled: isWorkCompletionEnabled } = useFeatureFlag(
    'work-completion-consumer',
  );

  // Open the modal on mount when the URL targets an appointment via
  // `?outcome=<appointmentId>`; the effect below then strips the param.
  const outcomeParam = new URLSearchParams(search).get(
    URL_QUERY_PARAMS.OUTCOME,
  );
  const [openAppointmentId, setOpenAppointmentId] = useState<string | null>(
    outcomeParam,
  );

  useEffect(() => {
    // Strip the param so a remount / back-nav doesn't reopen the modal.
    if (outcomeParam) {
      const params = new URLSearchParams(search);
      params.delete(URL_QUERY_PARAMS.OUTCOME);
      history.replace({
        search: params.toString() ? `?${params.toString()}` : '',
      });
    }
  }, [outcomeParam, search, history]);

  const pending =
    isWorkCompletionEnabled && isRenter
      ? appointments.filter(needsWorkCompletionPrompt)
      : [];

  if (!pending.length) {
    return null;
  }

  const openAppointment = pending.find(
    (appointment) => appointment.uniqueId === openAppointmentId,
  );

  return (
    <>
      {pending.map((appointment) => (
        <WorkCompletionPrompt
          key={appointment.uniqueId}
          partnerName={getPartnerName(appointment)}
          scheduledStartOn={appointment.scheduledStartOn}
          scheduledEndOn={appointment.scheduledEndOn}
          timeZone={maintenance?.home?.timeZone}
          onClick={() => setOpenAppointmentId(appointment.uniqueId)}
          customClassName="mb-lg"
        />
      ))}

      {openAppointment && (
        <AppointmentOutcomeModal
          isOpen
          onDismiss={() => setOpenAppointmentId(null)}
          maintenanceUniqueId={maintenance.uniqueId}
          appointmentUniqueId={openAppointment.uniqueId}
          partnerName={getPartnerName(openAppointment)}
          scheduledStartOn={openAppointment.scheduledStartOn}
          timeZone={maintenance?.home?.timeZone}
        />
      )}
    </>
  );
};
